import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Button, Card, CardContent, Chip, IconButton,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField,
  Alert, CircularProgress, Divider, Tooltip, Switch, FormControlLabel
} from '@mui/material';
import {
  Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon,
  ContentCopy as CopyIcon, SmartToy as AgentIcon, Visibility as ViewIcon
} from '@mui/icons-material';
import { agentsApi, handleApiError } from '../services/api';
import AgentEditor from './AgentEditor';

const AGENT_TYPE_LABELS = {
  classification: 'Classification Agents',
  categorization: 'Categorization Agents',
  pdf_categorization: 'PDF Categorization Agents'
};

const AGENT_TYPE_DESC = {
  classification: 'Determine if an email is a transaction notification',
  categorization: 'Extract and categorize transactions from emails',
  pdf_categorization: 'Extract and categorize transactions from PDF bank statements'
};

export default function AgentManager() {
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [editingAgent, setEditingAgent] = useState(null);
  const [viewingAgent, setViewingAgent] = useState(null);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [createName, setCreateName] = useState('');
  const [createSourceId, setCreateSourceId] = useState(null);
  const [creating, setCreating] = useState(false);
  const [deleting, setDeleting] = useState(null);

  const fetchAgents = useCallback(async () => {
    try {
      setLoading(true);
      const response = await agentsApi.getAll();
      setAgents(response.data.agents || []);
    } catch (err) {
      setError(handleApiError(err, 'Failed to load agents'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAgents(); }, [fetchAgents]);

  const handleCreate = async () => {
    if (!createName.trim() || !createSourceId) return;
    try {
      setCreating(true);
      await agentsApi.create({ name: createName.trim(), sourceAgentId: createSourceId });
      setSuccess('Custom agent created successfully');
      setCreateDialogOpen(false);
      setCreateName('');
      setCreateSourceId(null);
      fetchAgents();
    } catch (err) {
      setError(handleApiError(err, 'Failed to create agent'));
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (agentId) => {
    try {
      setDeleting(agentId);
      await agentsApi.delete(agentId);
      setSuccess('Agent deleted successfully');
      fetchAgents();
    } catch (err) {
      setError(handleApiError(err, 'Failed to delete agent'));
    } finally {
      setDeleting(null);
    }
  };

  const handleToggleActive = async (agent) => {
    try {
      if (agent.isActive || agent.is_active) {
        await agentsApi.deactivate(agent.id);
        setSuccess(`Agent "${agent.name}" deactivated — default prompts will be used`);
      } else {
        await agentsApi.activate(agent.id);
        setSuccess(`Agent "${agent.name}" is now active`);
      }
      fetchAgents();
    } catch (err) {
      setError(handleApiError(err, 'Failed to toggle agent'));
    }
  };

  const handleSave = async (agentId, data) => {
    try {
      await agentsApi.update(agentId, data);
      setSuccess('Agent updated successfully');
      setEditingAgent(null);
      fetchAgents();
    } catch (err) {
      setError(handleApiError(err, 'Failed to update agent'));
    }
  };

  const openCopyDialog = (sourceAgent) => {
    setCreateSourceId(sourceAgent.id);
    setCreateName(`My ${sourceAgent.name.replace('Default ', '')}`);
    setCreateDialogOpen(true);
  };

  const customCount = agents.filter(a => !(a.isDefault || a.is_default)).length;

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>;
  }

  if (viewingAgent) {
    return (
      <AgentEditor
        agent={viewingAgent}
        onCancel={() => setViewingAgent(null)}
        readOnly
      />
    );
  }

  if (editingAgent) {
    return (
      <AgentEditor
        agent={editingAgent}
        onSave={(data) => handleSave(editingAgent.id, data)}
        onCancel={() => setEditingAgent(null)}
      />
    );
  }

  return (
    <Box>
      {error && <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" onClose={() => setSuccess(null)} sx={{ mb: 2 }}>{success}</Alert>}

      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Custom agents let you fine-tune how your transactions are classified and categorized.
        Copy a default agent, add your own rules, and activate it to use during processing.
        ({customCount}/5 custom agents used)
      </Typography>

      {Object.entries(AGENT_TYPE_LABELS).map(([type, label]) => {
        const typeAgents = agents.filter(a => a.type === type);
        const defaultAgent = typeAgents.find(a => a.isDefault || a.is_default);
        const customAgents = typeAgents.filter(a => !(a.isDefault || a.is_default));
        const activeAgent = customAgents.find(a => a.isActive || a.is_active);

        return (
          <Box key={type} sx={{ mb: 4 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>{label}</Typography>
                <Typography variant="caption" color="text.secondary">{AGENT_TYPE_DESC[type]}</Typography>
              </Box>
              <Chip
                label={activeAgent ? `Active: ${activeAgent.name}` : 'Using Default'}
                color={activeAgent ? 'secondary' : 'default'}
                size="small"
                variant={activeAgent ? 'filled' : 'outlined'}
              />
            </Box>

            <Divider sx={{ mb: 2 }} />

            {/* Default agent card */}
            {defaultAgent && (
              <Card
                variant="outlined"
                sx={{ mb: 1.5, borderRadius: 2, bgcolor: '#fafafa', cursor: 'pointer', '&:hover': { boxShadow: 1, borderColor: 'primary.light' } }}
                onClick={() => setViewingAgent(defaultAgent)}
              >
                <CardContent sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 1.5, '&:last-child': { pb: 1.5 } }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <AgentIcon fontSize="small" color="action" />
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>{defaultAgent.name}</Typography>
                    <Chip label="Default" size="small" variant="outlined" sx={{ fontSize: '0.7rem' }} />
                    {!activeAgent && <Chip label="Active" size="small" color="success" sx={{ fontSize: '0.7rem' }} />}
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Tooltip title="View prompts">
                      <IconButton size="small" onClick={(e) => { e.stopPropagation(); setViewingAgent(defaultAgent); }}>
                        <ViewIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Create a custom copy">
                      <IconButton size="small" onClick={(e) => { e.stopPropagation(); openCopyDialog(defaultAgent); }} disabled={customCount >= 5}>
                        <CopyIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </CardContent>
              </Card>
            )}

            {/* Custom agent cards */}
            {customAgents.map(agent => (
              <Card
                key={agent.id}
                variant="outlined"
                sx={{
                  mb: 1.5, borderRadius: 2,
                  borderColor: (agent.isActive || agent.is_active) ? 'secondary.main' : 'rgba(0,0,0,0.08)',
                  borderWidth: (agent.isActive || agent.is_active) ? 2 : 1
                }}
              >
                <CardContent sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 1.5, '&:last-child': { pb: 1.5 } }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <AgentIcon fontSize="small" color={(agent.isActive || agent.is_active) ? 'secondary' : 'action'} />
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>{agent.name}</Typography>
                    {agent.sourceAgent && (
                      <Typography variant="caption" color="text.secondary">
                        (based on {agent.sourceAgent.name})
                      </Typography>
                    )}
                    {(agent.customRules || agent.custom_rules) && (
                      <Chip label="Has Rules" size="small" color="info" variant="outlined" sx={{ fontSize: '0.7rem' }} />
                    )}
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={!!(agent.isActive || agent.is_active)}
                          onChange={() => handleToggleActive(agent)}
                          size="small"
                          color="secondary"
                        />
                      }
                      label={<Typography variant="caption">{(agent.isActive || agent.is_active) ? 'Active' : 'Inactive'}</Typography>}
                      sx={{ mr: 1 }}
                    />
                    <Tooltip title="Edit">
                      <IconButton size="small" onClick={() => setEditingAgent(agent)}><EditIcon fontSize="small" /></IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton size="small" color="error" onClick={() => handleDelete(agent.id)} disabled={deleting === agent.id}>
                        {deleting === agent.id ? <CircularProgress size={18} /> : <DeleteIcon fontSize="small" />}
                      </IconButton>
                    </Tooltip>
                  </Box>
                </CardContent>
              </Card>
            ))}

            {customAgents.length === 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', pl: 1 }}>
                No custom agents — using default prompts
              </Typography>
            )}
          </Box>
        );
      })}

      {/* Create dialog */}
      <Dialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Create Custom Agent</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus margin="dense" fullWidth
            label="Agent Name"
            value={createName}
            onChange={(e) => setCreateName(e.target.value)}
            helperText="Give your custom agent a descriptive name"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleCreate} variant="contained" disabled={creating || !createName.trim()}>
            {creating ? <CircularProgress size={20} /> : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
